import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';

import { GET_LIST, GET_ONE, provider, history } from '../_utils';
import { SearchClear, PcSelect } from '../_Components';
import '../operators/operators.css';
import './reports.css';

const SORT_FIELD = {
    name: 'name',
    type: 'type',
    updated: 'updateAt',
    status: 'status',
};

const TYPE_META = {
    Standart: { label: 'Стандарт', chip: 'pc-emp-chip--mono' },
    SuiteCRM: { label: 'SuiteCRM', chip: 'pc-emp-chip--acc' },
    Chat: { label: 'Чат', chip: 'pc-emp-chip--vio' },
};

const statusMeta = (row) => (
    row?.status === 'Active'
        ? { label: 'Активен', chip: 'pc-emp-chip--ok' }
        : { label: 'Отключён', chip: 'pc-emp-chip--bad' }
);

const asList = (value) => (Array.isArray(value) ? value : []);

const normKey = (value) => String(value == null ? '' : value).trim().toLowerCase();

const campaignTypeOf = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'object') {
        return String(value.type || value.Type || '').toLowerCase();
    }
    return String(value).toLowerCase();
};

const isOutboundType = (type) => type === 'outbound' || type === 'out';
const isInboundType = (type) => type === 'inbound' || type === 'in' || type === 'finance';

const DIR_META = {
    in: { key: 'in', label: 'Входящий', chip: 'pc-emp-chip--acc' },
    out: { key: 'out', label: 'Исходящий', chip: 'pc-emp-chip--warn' },
};

const dirsOf = (key) => {
    if (key === 'both') {
        return [DIR_META.in, DIR_META.out];
    }
    if (key === 'out') {
        return [DIR_META.out];
    }
    if (key === 'in') {
        return [DIR_META.in];
    }
    return [DIR_META.in];
};

const directionFromCounts = (inCount, outCount) => {
    if (inCount && outCount) {
        return 'both';
    }
    if (outCount) {
        return 'out';
    }
    if (inCount) {
        return 'in';
    }
    return null;
};

const directionFromTypes = (types) => {
    const hasOut = types.some(isOutboundType);
    const hasIn = types.some(isInboundType);
    if (hasOut && hasIn) {
        return 'both';
    }
    if (hasOut) {
        return 'out';
    }
    if (hasIn) {
        return 'in';
    }
    return null;
};

const queueCounts = (row) => {
    const raw = (row && row.settings && row.settings.queues) || (row && row.queues) || {};
    if (Array.isArray(raw)) {
        let inCount = 0;
        let outCount = 0;
        raw.forEach((item) => {
            const type = campaignTypeOf(item);
            if (isOutboundType(type)) {
                outCount += 1;
            } else {
                inCount += 1;
            }
        });
        return { inCount, outCount };
    }
    return {
        inCount: asList(raw.inbounds).length,
        outCount: asList(raw.outbounds).length,
    };
};

const projectOf = (row, projects) => {
    const value = row && row.project;
    if (value && typeof value === 'object') {
        if (Array.isArray(value.campaigns) && value.campaigns.some((item) => item && item.type)) {
            return value;
        }
        const id = value._id || value.id;
        const name = normKey(value.name);
        return projects.find((item) => (
            String(item._id || item.id) === String(id)
            || (name && normKey(item.name) === name)
        )) || value;
    }
    if (!value) {
        return null;
    }
    const key = String(value);
    const name = normKey(value);
    return projects.find((item) => (
        String(item._id || item.id) === key || normKey(item.name) === name
    )) || null;
};

const linkedProjectId = (value) => {
    if (value && typeof value === 'object') {
        return String(value._id || value.id || '');
    }
    return String(value || '');
};

const campaignsOfProject = (project, campaigns) => {
    const fromProject = asList(project && project.campaigns);
    if (fromProject.some((item) => item && typeof item === 'object' && (item.type || item.Type))) {
        return fromProject;
    }
    const pid = String((project && (project._id || project.id)) || '');
    const pname = normKey(project && project.name);
    if (!pid && !pname) {
        return [];
    }
    return asList(campaigns).filter((item) => {
        const linked = item && item.project;
        const id = linkedProjectId(linked);
        const name = normKey(linked && typeof linked === 'object' ? linked.name : '');
        return (pid && id === pid) || (pname && (name === pname || normKey(id) === pname));
    });
};

const directionOf = (row, projects, campaigns) => {
    const counts = queueCounts(row);
    const fromQueues = directionFromCounts(counts.inCount, counts.outCount);
    if (fromQueues) {
        return dirsOf(fromQueues);
    }

    const fromCampaign = directionFromTypes([
        campaignTypeOf(row && row.campaign),
        campaignTypeOf(row && row.campaignType),
    ].filter(Boolean));
    if (fromCampaign) {
        return dirsOf(fromCampaign);
    }

    const fromProject = directionFromTypes(
        campaignsOfProject(projectOf(row, projects), campaigns).map(campaignTypeOf).filter(Boolean)
    );
    if (fromProject) {
        return dirsOf(fromProject);
    }

    return dirsOf('in');
};

const reportSettingsCache = {};

const enrichReportSettings = async (rows) => {
    const list = asList(rows);
    await Promise.all(list.map(async (row) => {
        const id = row && (row._id || row.id);
        if (!id) {
            return;
        }
        if (row.settings && row.settings.queues) {
            reportSettingsCache[id] = row.settings;
            return;
        }
        if (reportSettingsCache[id]) {
            return;
        }
        try {
            const { data } = await provider(GET_ONE, '/reports', id, { persist: true });
            if (data && data.settings) {
                reportSettingsCache[id] = data.settings;
            }
        } catch (error) {
            reportSettingsCache[id] = row.settings || {};
        }
    }));
    return list.map((row) => {
        const id = row && (row._id || row.id);
        const settings = (id && reportSettingsCache[id]) || row.settings;
        return settings ? { ...row, settings } : row;
    });
};

const countOf = (value) => {
    if (Array.isArray(value)) return value.length;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const fmtStamp = (value) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '');

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const ReportsList = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [projects, setProjects] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [project, setProject] = useState('');
    const [type, setType] = useState('');
    const [act, setAct] = useState('Active');
    const [sortKey, setSortKey] = useState('name');
    const [sortDir, setSortDir] = useState(1);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const loadMeta = async () => {
            try {
                const [{ data: projectRows, status: projectStatus }, { data: campaignRows }] = await Promise.all([
                    provider(GET_LIST, '/projects', {
                        filter: {},
                        sort: { field: 'name', order: 'ASC' },
                        pagination: { page: 1, perPage: 999 },
                    }, { persist: true }),
                    provider(GET_LIST, '/campaigns', {
                        filter: {},
                        sort: { field: 'name', order: 'ASC' },
                        pagination: { page: 1, perPage: 9999 },
                    }, { persist: true }).catch(() => ({ data: [] })),
                ]);
                if (projectStatus === 200 || projectRows) {
                    setProjects(projectRows || []);
                }
                setCampaigns(campaignRows || []);
            } catch (error) {
                console.error('Error loading projects:', error);
            }
        };
        loadMeta();
    }, []);

    const load = useCallback(async () => {
        const filter = {};
        if (act) filter.status = act;
        if (type) filter.type = type;
        if (project) filter.project = project;
        if (debouncedQuery) filter.name = debouncedQuery;

        try {
            const { data, total: count } = await provider(GET_LIST, '/reports', {
                filter,
                sort: { field: SORT_FIELD[sortKey] || 'name', order: sortDir > 0 ? 'ASC' : 'DESC' },
                pagination: { page, perPage },
            });
            const rowsWithSettings = await enrichReportSettings(data || []);
            setRows(rowsWithSettings);
            setTotal(count || 0);
        } catch (error) {
            console.error('Error loading reports:', error);
        }
    }, [act, type, project, debouncedQuery, sortKey, sortDir, page, perPage]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [act, type, project, debouncedQuery, sortKey, sortDir, perPage]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir((dir) => -dir);
        else { setSortKey(key); setSortDir(key === 'updated' ? -1 : 1); }
    };

    const sortClass = (key) => (sortKey === key ? 's' : '');
    const arrow = sortDir > 0 ? '↑' : '↓';
    const pages = Math.max(1, Math.ceil(total / perPage));

    return (
        <div className="pc-page pc-emp pc-rpt">
            <div className="pc-emp-topbar">
                <h1>Отчёты</h1>
                <div className="pc-emp-srch">
                    <Icon d="M7 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zM10.5 10.5L14 14" />
                    <input
                        className="pc-emp-inp"
                        value={query}
                        placeholder="Поиск по названию"
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <SearchClear visible={Boolean(query)} onClear={() => setQuery('')} />
                </div>
                <PcSelect
                    className="pc-rpt-project"
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    options={projects.map((item) => ({ id: item._id || item.id, name: item.name }))}
                    allowEmpty
                    emptyLabel="Все проекты"
                    searchable
                    searchPlaceholder="Найти проект"
                />
                <PcSelect
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    options={[
                        { id: 'Standart', name: 'Стандарт' },
                        { id: 'SuiteCRM', name: 'SuiteCRM' },
                        { id: 'Chat', name: 'Чат' },
                    ]}
                    allowEmpty
                    emptyLabel="Все типы"
                    searchable={false}
                />
                <PcSelect
                    value={act}
                    onChange={(e) => setAct(e.target.value)}
                    options={[
                        { id: 'Active', name: 'Активные' },
                        { id: 'Disabled', name: 'Отключённые' },
                    ]}
                    allowEmpty
                    emptyLabel="Все статусы"
                    searchable={false}
                />
                <span className="pc-emp-chip pc-emp-chip--mono">
                    {total
                        ? `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} из ${total}`
                        : '0 отчётов'}
                </span>
            </div>

            <div className="pc-emp-stage">
                <div className="pc-emp-panel">
                    <div className="pc-emp-thead">
                        <span className={`pc-rpt-c-name ${sortClass('name')}`} data-sort="name" onClick={() => toggleSort('name')}>
                            Отчёт<span className="ar">{arrow}</span>
                        </span>
                        <span className="pc-rpt-c-proj">Проект</span>
                        <span className={`pc-rpt-c-type ${sortClass('type')}`} data-sort="type" onClick={() => toggleSort('type')}>
                            Тип<span className="ar">{arrow}</span>
                        </span>
                        <span className="pc-rpt-c-dir">Направление</span>
                        <span className="pc-rpt-c-num">Колонки</span>
                        <span className={`pc-rpt-c-upd ${sortClass('updated')}`} data-sort="updated" onClick={() => toggleSort('updated')}>
                            Обновлён<span className="ar">{arrow}</span>
                        </span>
                        <span className={`pc-rpt-c-st ${sortClass('status')}`} data-sort="status" onClick={() => toggleSort('status')}>
                            Статус<span className="ar">{arrow}</span>
                        </span>
                        <span className="pc-emp-c-go" />
                    </div>
                    <div className="pc-emp-pbd">
                        {rows.length === 0 ? (
                            <div className="pc-emp-empty">
                                <b>Ничего не найдено</b>
                                <p>Измените запрос или сбросьте фильтры.</p>
                            </div>
                        ) : rows.map((row) => {
                            const id = row._id || row.id;
                            const status = statusMeta(row);
                            const kind = TYPE_META[row.type] || TYPE_META.Standart;
                            const active = row.status === 'Active';
                            const direction = directionOf(row, projects, campaigns);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    className={`pc-emp-trow${active ? '' : ' off'}`}
                                    onClick={() => history.push(`/reports/${id}`)}
                                >
                                    <span className="pc-rpt-c-name">
                                        <span className="n">{row.name || '—'}</span>
                                    </span>
                                    <span className="pc-rpt-c-proj">
                                        {row.project ? (typeof row.project === 'object' ? row.project.name : row.project) : <span className="pc-emp-dim">—</span>}
                                    </span>
                                    <span className="pc-rpt-c-type">
                                        <span className={`pc-emp-chip ${kind.chip}`}>{kind.label}</span>
                                    </span>
                                    <span className="pc-rpt-c-dir">
                                        {direction.map((item) => (
                                            <span key={item.key} className={`pc-emp-chip ${item.chip}`}>
                                                {item.label}
                                            </span>
                                        ))}
                                    </span>
                                    <span className="pc-rpt-c-num">{countOf(row.columns)}</span>
                                    <span className="pc-rpt-c-upd">
                                        {row.updateAt ? fmtStamp(row.updateAt) : <span className="pc-emp-dim">—</span>}
                                    </span>
                                    <span className="pc-rpt-c-st">
                                        <span className={`pc-emp-chip ${status.chip}`}>
                                            <span className="pc-emp-dot" />
                                            {status.label}
                                        </span>
                                    </span>
                                    <span className="pc-emp-c-go">
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                            <path d="M6 3.5L10.5 8 6 12.5" />
                                        </svg>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="pc-emp-pager">
                        <PcSelect
                            value={String(perPage)}
                            onChange={(e) => setPerPage(Number(e.target.value))}
                            options={[25, 50, 100].map((n) => ({ id: String(n), name: `${n} на странице` }))}
                            searchable={false}
                        />
                        <span className="pc-emp-pager__n">{page} / {pages}</span>
                        <div className="pc-emp-navb">
                            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title="Назад">
                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                    <path d="M10 3.5L5.5 8l4.5 4.5" />
                                </svg>
                            </button>
                            <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} title="Вперёд">
                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                    <path d="M6 3.5L10.5 8 6 12.5" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportsList;
